import React from "react";
import { Loader, Lock } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Link } from "@/components/common/Link";
export default function ConfidentialConsultation() {
  return <section id="confidential-consultation" className="bg-muted py-20 px-4 md:py-28">
      <div className="mx-auto max-w-[700px]">
        <div className="mb-12 text-center">
          <h2 className="text-primary font-serif text-4xl font-medium leading-tight md:text-5xl">
            The First Step Toward Your Legacy
          </h2>
          <p className="text-muted-foreground mt-4 text-lg">
            Secure a private conversation with our wealth architects.
          </p>
        </div>

        <FormProvider formId="confidential_consultation_home" sectionName="confidential-consultation" defaultValues={{
        full_name: "",
        email_address: "",
        phone_number: "",
        preferred_contact_date: "",
        consultation_details: "",
        confidentiality_agreement: false
      }} validationRules={{
        full_name: {
          required: true,
          minLength: 2
        },
        email_address: {
          required: true,
          pattern: "email"
        },
        phone_number: {
          required: true,
          minLength: 10
        },
        preferred_contact_date: {
          required: false
        },
        consultation_details: {
          required: true,
          minLength: 10
        },
        confidentiality_agreement: {
          required: true
        }
      }} submitText="Request Consultation" successMessage="Your confidential request has been received. We will contact you shortly." errorMessage="We were unable to process your request. Please try again." className="bg-background/50 rounded-xl border border-primary/5 p-6 shadow-sm backdrop-blur-sm md:p-10">
          <ConsultationForm />
        </FormProvider>
      </div>
    </section>;
}
function ConsultationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="bg-primary/5 mb-6 flex h-16 w-16 items-center justify-center rounded-full">
          <Lock className="text-primary h-8 w-8" />
        </div>
        <h3 className="text-primary font-serif text-2xl font-medium">
          Request Received
        </h3>
        <p className="text-muted-foreground mt-2 max-w-md">
          {successMessage}
        </p>
        <Button variant="outline" className="mt-8 border-primary/20 hover:bg-primary/5" onClick={() => window.location.reload()}>
          Return to Home
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {}
        <FormField control={control} name="full_name" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Jane Doe" className="border-input h-12 bg-white/50" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="email_address" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="jane.doe@example.com" className="border-input h-12 bg-white/50" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {}
        <FormField control={control} name="phone_number" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(555) 123-4567" className="border-input h-12 bg-white/50" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="preferred_contact_date" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary">
                Preferred Contact Date (Optional)
              </FormLabel>
              <FormControl>
                <Input type="date" className="border-input h-12 bg-white/50" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {}
      <FormField control={control} name="consultation_details" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-primary">
              Brief Description of Your Needs
            </FormLabel>
            <FormControl>
              <textarea className="border-input placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 aria-invalid:border-destructive flex min-h-[120px] w-full rounded-md border bg-white/50 px-3 py-3 text-base shadow-xs outline-none transition-[color,box-shadow] focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 md:text-sm" placeholder="e.g., tax-strategy planning, estate planning, investment advisory..." {...field} />
            </FormControl>
            <FormDescription>
              Provide a short overview so we can prepare for your consultation.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="confidentiality_agreement" render={({
      field: {
        value,
        onChange,
        ...fieldProps
      }
    }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border border-primary/10 p-4">
            <FormControl>
              <input type="checkbox" className="border-primary text-primary focus:ring-primary mt-1 h-4 w-4 rounded shadow-sm" checked={value} onChange={e => onChange(e.target.checked)} {...fieldProps} />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel className="text-primary font-normal">
                I acknowledge that all information shared will be kept strictly
                confidential.
              </FormLabel>
              <FormDescription>
                Your privacy is our top priority.
              </FormDescription>
            </div>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="bg-destructive/10 text-destructive rounded-md p-4 text-sm">
          {submitError}
        </div>}

      <div className="pt-4 text-center">
        <Button type="submit" disabled={isSubmitting} className="bg-primary text-secondary hover:bg-primary/90 h-14 w-full px-8 text-lg font-medium shadow-md transition-all sm:w-auto">
          {isSubmitting ? <>
              <Loader className="mr-2 h-5 w-5 animate-spin" />
              Processing...
            </> : "Request Confidential Consultation"}
        </Button>
        <p className="text-muted-foreground mt-4 text-xs">
          By submitting this form, you agree to our{" "}
          <Link to="#" className="hover:text-primary underline">
            Privacy Policy
          </Link>
          .
        </p>
      </div>
    </Form>;
}